import React, { useState, useMemo } from 'react';
import type { Campaign } from '@/types/campaign';
import type { ColdStartOptimizationRequest, ColdStartOptimizationResponse } from '@/types/api';
import { FlowerButton } from '@/components/ui/FlowerButton';
import { Badge } from '@/components/ui/Badge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { usePendingChangesStore } from '@/store/pendingChangesStore';
import { debug } from '@/utils/debug';
import { formatEnumPretty } from '@/utils/formatEnum';
import { getStatusBadgeClasses } from '@/utils/statusStyles';
import { useColdstartEnabled } from '@/hooks/useOptimizationConfig';
import '@/styles/tooltip.css';

interface ColdStartOptimizationProps {
  campaign: Campaign;
  selectedAdGroups: Set<string>;
  onAdGroupToggle: (adGroupId: string, enabled: boolean) => void;
  onOptimize: (request: ColdStartOptimizationRequest) => Promise<ColdStartOptimizationResponse>;
  isLoading: boolean;
  result: ColdStartOptimizationResponse | null;
  error: Error | null;
}

export function ColdStartOptimization({
  campaign,
  selectedAdGroups,
  onAdGroupToggle,
  onOptimize,
  isLoading,
  result,
  error,
}: ColdStartOptimizationProps) {
  // Check if coldstart feature is enabled (fetched from backend config)
  const coldstartEnabled = useColdstartEnabled();

  const [dowWise, setDowWise] = useState(false);
  const [implementOptBids, setImplementOptBids] = useState(false);

  const { getPendingStatusChange, queueStatusChange } = usePendingChangesStore();

  const devMode = debug.isDev();

  // Check if campaign is eligible for cold start optimization
  const isEligible = useMemo(() => {
    // Safety check - must have campaign and ads_group_list
    if (!campaign || !campaign.ads_group_list || campaign.ads_group_list.length === 0) {
      if (devMode) {
        debug.log('Cold Start: No ad groups found');
      }
      return false;
    }

    // Check if campaign type contains 'SEARCH' (case-insensitive)
    const isSearchCampaign = campaign.campaign_type?.toUpperCase().includes('SEARCH') || false;
    // Check for Manual CPC - case-insensitive to handle both 'Manual CPC' and 'MANUAL_CPC'
    const hasManualCPC = campaign.ads_group_list.some((ag) =>
      ag.bidding_strategy_type?.toUpperCase().includes('MANUAL')
    );

    // Log for debugging
    if (devMode) {
      debug.log('Cold Start Eligibility Check:', {
        campaign_type: campaign.campaign_type,
        isSearchCampaign,
        hasManualCPC,
        ad_groups: campaign.ads_group_list.map(ag => ({
          id: ag.ad_group_id || ag.ads_group_id,
          name: ag.ad_group_name || ag.ads_group_name,
          strategy: ag.bidding_strategy_type
        }))
      });
    }

    return isSearchCampaign && hasManualCPC;
  }, [campaign, devMode]);

  // Build campaigns data structure for API
  const campaignsData = useMemo(() => {
    const data: Record<string, Record<string, string[]>> = {};

    if (campaign.ads_group_list && campaign.ads_group_list.length > 0) {
      data[campaign.campaign_id] = {};

      campaign.ads_group_list.forEach((adGroup) => {
        const adGroupId = adGroup.ad_group_id || adGroup.ads_group_id || '';

        if (selectedAdGroups.has(adGroupId) && adGroup.keywords_list && adGroup.keywords_list.length > 0) {
          data[campaign.campaign_id][adGroupId] = adGroup.keywords_list.map((keyword) => {
            if (typeof keyword === 'object' && 'keyword_id' in keyword) {
              return keyword.keyword_id;
            } else if (typeof keyword === 'object' && 'id' in keyword) {
              return (keyword as { id: string }).id;
            } else {
              return typeof keyword === 'string' ? keyword : (keyword as { text?: string; keyword_text?: string }).text || (keyword as { text?: string; keyword_text?: string }).keyword_text || String(keyword);
            }
          });
        }
      });
    }

    return data;
  }, [campaign, selectedAdGroups]);

  const handleOptimize = async () => {
    const request: ColdStartOptimizationRequest = {
      google_ads_account_id: campaign.google_ads_account_id || '',
      campaigns_ad_groups_to_optimize: campaignsData,
      dow_wise: dowWise,
      implement_opt_bids: implementOptBids,
    };

    await onOptimize(request);
  };

  // If coldstart feature is disabled globally, don't render the component
  if (!coldstartEnabled) {
    return null;
  }

  if (!isEligible) {
    // In production mode, don't show anything for ineligible campaigns (e.g., Performance Max)
    if (!devMode) {
      return null;
    }

    // In dev mode, show debug info about why campaign is not eligible
    return (
      <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-yellow-800 mb-2 text-center">
          Cold Start Optimization Not Available
        </h3>
        <p className="text-yellow-700 text-center mb-4">
          Cold start optimization is only available for SEARCH campaigns with Manual CPC bidding strategy. This campaign does not meet these requirements.
        </p>
        <div className="bg-yellow-100 border border-yellow-300 rounded p-4 text-sm">
          <p className="font-semibold text-yellow-900 mb-2">🔧 Debug Info:</p>
          <div className="space-y-1 text-yellow-800">
            <p><span className="font-medium">Campaign Type:</span> {campaign.campaign_type || 'Not set'}</p>
            <p><span className="font-medium">Ad Groups:</span> {campaign.ads_group_list?.length || 0}</p>
            {campaign.ads_group_list && campaign.ads_group_list.length > 0 && (
              <div className="mt-2">
                <p className="font-medium">Ad Group Bidding Strategies:</p>
                <ul className="list-disc list-inside ml-4">
                  {campaign.ads_group_list.map((ag, idx) => (
                    <li key={idx}>
                      {ag.ad_group_name || ag.ads_group_name || 'Unnamed'}: {ag.bidding_strategy_type || 'Not set'}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const selectedCount = selectedAdGroups.size;
  const hasSelection = selectedCount > 0;

  // Dev mode render
  if (devMode) {
    return (
      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
        <h3 className="text-xl font-semibold text-blue-900 mb-2 flex items-center gap-2">
          <span>🚀</span> Cold Start Optimization
        </h3>
        <p className="text-blue-700 mb-6">
          This is a SEARCH campaign with Manual CPC bidding. You can run cold start optimization to improve bid performance.
        </p>

        {/* Optimization Settings */}
        <div className="space-y-4 mb-6">
          {/* Day of Week Optimization */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="dowWise"
              checked={dowWise}
              onChange={(e) => setDowWise(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="dowWise" className="flex-1">
              <span className="font-medium text-gray-900">Day of Week Optimization</span>
              <p className="text-sm text-gray-600 mt-0.5">
                Optimize bids based on day-of-week performance patterns
              </p>
            </label>
          </div>

          {/* Implement Optimized Bids */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="implementOptBids"
              checked={implementOptBids}
              onChange={(e) => setImplementOptBids(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="implementOptBids" className="flex-1">
              <span className="font-medium text-gray-900">Implement Optimized Bids</span>
              <p className="text-sm text-gray-600 mt-0.5">
                Automatically apply optimized bids to your Google Ads account
              </p>
            </label>
          </div>
        </div>

        {/* Selection Summary */}
        <div className="bg-white rounded-lg p-4 mb-6 border border-blue-200">
          <p className="text-sm text-gray-700">
            <span className="font-medium">{selectedCount}</span> ad groups selected for optimization
          </p>
          {!hasSelection && (
            <p className="text-sm text-orange-600 mt-1">
              ⚠️ Please enable at least one ad group to run optimization
            </p>
          )}
        </div>

        {/* Run Optimization Button */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleOptimize}
            disabled={isLoading || !hasSelection}
            className="px-6 py-3 bg-blue-700 text-white font-semibold rounded-lg hover:bg-blue-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <LoadingSpinner size="sm" variant="white" label="Running optimization" /> Running Optimization...
              </span>
            ) : (
              'Run Cold Start Optimization'
            )}
          </button>
          {isLoading && (
            <span className="text-sm text-gray-600">This may take a moment...</span>
          )}
        </div>

        {/* Development Mode: Show Data Structure */}
        <div className="mt-6 p-4 bg-gray-100 rounded-lg border border-gray-300">
          <h4 className="font-semibold text-gray-900 mb-2">🔧 Debug: Optimization Data</h4>
          <pre className="text-xs bg-gray-900 text-green-400 p-3 rounded overflow-auto max-h-60">
            {JSON.stringify(campaignsData, null, 2)}
          </pre>
        </div>

        {/* Results Display */}
        {result && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <h4 className="font-semibold text-green-900 mb-2">✅ Optimization Complete</h4>
            <div className="text-sm text-green-800 space-y-1">
              <p>{result.message}</p>
              {result.performance_analysis && (
                <>
                  <p>
                    <span className="font-medium">Total Keywords:</span> {result.performance_analysis.total_keywords}
                  </p>
                  <p>
                    <span className="font-medium">Active Keywords:</span> {result.performance_analysis.active_keywords}
                  </p>
                  <p>
                    <span className="font-medium">Budget Utilization:</span> {(result.performance_analysis.budget_utilization * 100).toFixed(1)}%
                  </p>
                  <p>
                    <span className="font-medium">Expenditure:</span> ${result.performance_analysis.expenditure.toFixed(2)}
                  </p>
                </>
              )}
            </div>

            {/* Optimized Bids Summary */}
            {result.max_cpc_opt_dict && Object.keys(result.max_cpc_opt_dict).length > 0 && (
              <div className="mt-4 p-3 bg-white border border-green-200 rounded">
                <h5 className="font-medium text-green-900 mb-2">📊 Optimized Bids</h5>
                <div className="text-xs space-y-1">
                  {Object.entries(result.max_cpc_opt_dict).map(([dayOfWeek, keywords]: [string, unknown]) => {
                    if (typeof keywords !== 'object') return null;

                    return (
                      <div key={dayOfWeek}>
                        <p className="font-semibold text-gray-800 mb-1 mt-2">
                          {dayOfWeek === 'all_dows' ? 'All Days of Week' : `Day: ${dayOfWeek}`}
                        </p>
                        <div className="ml-2 space-y-1">
                          {Object.entries(keywords as Record<string, unknown>).map(([keywordId, optBid]: [string, unknown]) => {
                            // Find keyword in campaign data
                            let keywordText = `Keyword ${keywordId}`;
                            let adGroupName = '';

                            campaign?.ads_group_list?.forEach(adGroup => {
                              const keyword = adGroup.keywords_list?.find(
                                kw => {
                                  const kwId = kw.keyword_id || (typeof kw === 'object' && kw !== null && 'id' in kw ? (kw as { id: string }).id : undefined);
                                  return kwId === keywordId;
                                }
                              );
                              if (keyword) {
                                keywordText =
                                  (keyword && typeof keyword === 'object' && 'text' in keyword && typeof (keyword as { text?: unknown }).text === 'string'
                                    ? (keyword as { text: string }).text
                                    : keyword.keyword_text) || keywordId;
                                adGroupName = adGroup.ad_group_name || adGroup.ads_group_name || '';
                              }
                            });

                            return (
                              <div key={keywordId} className="flex justify-between items-center py-1 border-b border-gray-100 last:border-0">
                                <div className="flex-1">
                                  <div className="text-gray-700 font-medium">{keywordText}</div>
                                  {adGroupName && (
                                    <div className="text-gray-500 text-xs">{adGroupName} | {keywordId}</div>
                                  )}
                                </div>
                                <span className="font-bold text-green-700 ml-2">${typeof optBid === 'number' ? optBid.toFixed(2) : String(optBid)}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Performance Warnings */}
            {result.performance_analysis && result.performance_analysis.warnings.length > 0 && (
              <div className="mt-4 space-y-2">
                <h5 className="font-medium text-yellow-900">⚠️ Performance Warnings</h5>
                {result.performance_analysis.warnings.map((warning, idx) => (
                  <div key={idx} className="text-xs bg-yellow-50 border border-yellow-200 rounded p-2">
                    <p className="font-medium text-yellow-900">{warning.category} - {warning.severity}</p>
                    <p className="text-yellow-800">{warning.message}</p>
                    {warning.recommendations.length > 0 && (
                      <ul className="mt-1 ml-4 list-disc text-yellow-700">
                        {warning.recommendations.map((rec, ridx) => (
                          <li key={ridx}>{rec}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Full JSON Response */}
            <div className="mt-4">
              <h5 className="font-medium text-green-900 mb-2">Full Response Data</h5>
              <pre className="text-xs bg-gray-900 text-green-400 p-3 rounded overflow-auto max-h-96 border border-green-300">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <h4 className="font-semibold text-red-900 mb-2">❌ Error</h4>
            <p className="text-sm text-red-800">{error.message}</p>
          </div>
        )}
      </div>
    );
  }

  // Production mode render
  return (
    <div className="rounded-lg shadow-sm overflow-hidden">
          {/* Title Banner - dark blue */}
          <div className="bg-blue-700 px-5 py-3 flex items-center gap-2">
            <svg className="w-8 h-8 flex-shrink-0 -mt-1" viewBox="-4 -4 36 32" fill="none" shapeRendering="crispEdges">
              <path d="M2 11L2.5 9.5L4 9L2.5 8.5L2 7L1.5 8.5L0 9L1.5 9.5L2 11Z" fill="white" />
              <path d="M6 21L6.75 18.75L9 18L6.75 17.25L6 15L5.25 17.25L3 18L5.25 18.75L6 21Z" fill="white" />
              <path d="M17 24L19 17L26 15L19 13L17 6L15 13L8 15L15 17L17 24Z" fill="white" />
            </svg>
            <h3 className="text-lg font-semibold text-white">Flouri Optimize Bids</h3>
          </div>

          {/* Content area - light blue */}
          <div className="bg-blue-50 border border-t-0 border-blue-200 rounded-b-lg">
            <div className="px-5 pt-3 pb-3">
              <p className="text-sm text-blue-700">
                Press the{' '}
                <span className="inline-flex items-center justify-center bg-white border border-blue-200 rounded-full w-[22px] h-[22px] align-middle mx-0.5 -mt-0.5">
                  <svg
                    className="w-3.5 h-3.5"
                    viewBox="0 0 637.48 637.65"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path fill="#b7bcc5" d="M452.13,0h-.31c-102.67,0-185.9,83.23-185.9,185.9v169.54c0,8.9,7.22,16.12,16.12,16.12h170.09c102.36,0,185.34-82.98,185.34-185.34v-.87C637.48,82.98,554.49,0,452.13,0ZM490.17,234.49c0,45.74-37.08,82.82-82.82,82.82h-71.99c-6.12,0-11.07-4.96-11.07-11.07v-71.74c0-45.88,37.19-83.06,83.06-83.06h0c45.74,0,82.82,37.08,82.82,82.82v.25Z" />
                    <path fill="#b7bcc5" d="M215.11,356.47v-92.47c0-59.4-48.15-107.55-107.55-107.55h0C48.15,156.45,0,204.6,0,264h0c0,59.4,48.15,107.55,107.55,107.55h92.47c8.33,0,15.08-6.75,15.08-15.08Z" />
                    <path fill="#b7bcc5" d="M265.92,438.11v91.98c0,59.4,48.15,107.55,107.55,107.55h0c59.4,0,107.55-48.15,107.55-107.55h0c0-59.4-48.15-107.55-107.55-107.55h-91.98c-8.6,0-15.57,6.97-15.57,15.57Z" />
                    <path fill="#b7bcc5" d="M199.53,422.54h-91.98c-59.4,0-107.55,48.15-107.55,107.55h0c0,59.4,48.15,107.55,107.55,107.55h0c59.4,0,107.55-48.15,107.55-107.55v-91.98c0-8.6-6.97-15.57-15.57-15.57Z" />
                  </svg>
                </span>{' '}
                next to Manual CPC ad groups to enable daily optimization.
              </p>
            </div>

            {/* Manual CPC Ad Groups List */}
            {campaign.ads_group_list && campaign.ads_group_list.length > 0 && (
              <div className="px-5 pb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Manual CPC Ad Groups</h4>
                <div className="space-y-3">
                  {campaign.ads_group_list
                    .filter((adGroup) =>
                      adGroup.bidding_strategy_type?.toUpperCase().includes('MANUAL') &&
                      adGroup.status !== 'REMOVED'
                    )
                    .map((adGroup) => {
                      const adGroupId = adGroup.ad_group_id || adGroup.ads_group_id || '';
                      const adGroupName = adGroup.ad_group_name || adGroup.ads_group_name || 'Unnamed Ad Group';
                      const keywordCount = adGroup.keywords_list?.length || 0;
                      const isChecked = selectedAdGroups?.has(adGroupId) ?? false;

                      // Check for pending status change
                      const pendingChange = getPendingStatusChange('ad_group', adGroupId);
                      const effectiveStatus = pendingChange
                        ? (pendingChange.action === 'enable' ? 'ENABLED' : pendingChange.action === 'pause' ? 'PAUSED' : adGroup.status)
                        : adGroup.status;

                      return (
                        <div
                          key={adGroupId}
                          className="border rounded-lg p-4 transition-colors border-blue-300 bg-white hover:bg-blue-50/50"
                        >
                          <div className="flex items-start gap-3">
                            <div className="mt-1">
                              <FlowerButton
                                size={25}
                                isOpen={isChecked}
                                onToggle={(enabled) => onAdGroupToggle(adGroupId, enabled)}
                                showCenterDot={false}
                              />
                            </div>
                            <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
                              {/* Left: Name and metadata */}
                              <div className="min-w-0">
                                <h4 className="font-medium text-gray-900 mb-1">{adGroupName}</h4>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                                  {adGroup.max_cpc_bid && (
                                    <span>
                                      <span className="font-medium">Max CPC:</span> ${adGroup.max_cpc_bid.toFixed(2)}
                                    </span>
                                  )}
                                  <span>
                                    <span className="font-medium">Keywords:</span> {keywordCount}
                                  </span>
                                </div>
                              </div>
                              {/* Right: Badges */}
                              <div className="flex flex-col items-end gap-1 shrink-0">
                                {(() => {
                                  // Determine display status and label using centralized utilities
                                  let displayStatus: string = adGroup.status || 'ENABLED';
                                  let displayLabel = formatEnumPretty(adGroup.status);

                                  if (pendingChange) {
                                    displayStatus = pendingChange.action === 'enable' ? 'ENABLING' :
                                                    pendingChange.action === 'pause' ? 'PAUSING' : 'REMOVING';
                                    displayLabel = pendingChange.action === 'enable' ? 'Enabling...' :
                                                   pendingChange.action === 'pause' ? 'Pausing...' : 'Removing...';
                                  }

                                  return (
                                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusBadgeClasses(displayStatus)}`}>
                                      {displayLabel}
                                    </span>
                                  );
                                })()}
                                {isChecked && effectiveStatus === 'ENABLED' && (
                                  <Badge variant="primary">✓ Flouri Optimized</Badge>
                                )}
                                {isChecked && effectiveStatus !== 'ENABLED' && (
                                  <button
                                    type="button"
                                    onClick={() => queueStatusChange({
                                      entityType: 'ad_group',
                                      entityId: adGroupId,
                                      campaignId: campaign.campaign_id,
                                      action: 'enable',
                                      entityName: adGroupName,
                                      platform: 'google_ads',
                                    })}
                                    className="text-xs text-amber-600 hover:text-amber-800 hover:underline transition-colors"
                                  >
                                    Enable ad group to optimize bids
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
    </div>
  );
}
